import { useAuth } from 'react-oidc-context';
import { markSignIn, resetAnalytics, track } from '@consolidocs-ui/analytics';
import {
  ReactNode,
  useCallback,
  useEffect,
  useReducer,
  useRef,
  useState,
} from 'react';
import { Loader } from '../Loader/Loader';
import { Box, Button, Typography, Sheet } from '@mui/joy';
import LoginIcon from '@mui/icons-material/Login';
import { useTranslation } from 'react-i18next';
import { cognitoLogoutParams } from './cognitoLogout';
import { SignInCard, SignInNotice } from './SignInCard';
import { SignInScreen } from './SignInScreen';
import { isMicrosoftCallback } from './microsoftSignIn';
import { isLoginPage, loginUrl, signInReturnPath } from './signInRedirect';
import type { SignInOptions } from './useSignIn';
import { signInRefusal } from './signInFailure';

export { MICROSOFT_CALLBACK_PATH } from './microsoftSignIn';
export { LOGIN_PATH, safeReturnPath } from './signInRedirect';
export { useSignOut } from './useSignOut';
export type { SignInBackdrop, SignInOptions } from './useSignIn';

export interface AuthenticateProps {
  children: ReactNode;
  /**
   * Show the app's own sign-in page (email and password, plus the providers
   * listed here). Leave it out and signed-out users go straight to the
   * Cognito login page, as before.
   */
  signIn?: SignInOptions;
}

/**
 * OIDC error codes that mean the session is simply over — the refresh token has
 * expired or been revoked, or Cognito wants the user to sign in again. Nothing
 * is broken, so they get the "sign in again" card rather than the error one.
 */
const SESSION_ENDED_ERRORS = ['invalid_grant', 'login_required'];

const isSessionEndedError = (error?: Error | null): boolean => {
  if (!error) return false;

  /* oidc-client-ts's ErrorResponse carries the OAuth code on `error` */
  const code = (error as { error?: unknown }).error;
  if (typeof code === 'string' && SESSION_ENDED_ERRORS.includes(code)) {
    return true;
  }

  /* some paths only surface the code inside the message */
  return SESSION_ENDED_ERRORS.some((known) =>
    (error.message ?? '').includes(known)
  );
};

export const Authenticate = ({ children, signIn }: AuthenticateProps) => {
  const { t } = useTranslation();
  const auth = useAuth();
  const [tokenExpired, setTokenExpired] = useState(false);
  const [expiredMessage, setExpiredMessage] = useState('');
  const [isReconnecting, setIsReconnecting] = useState(false);

  // The listener below is registered once, so it must not close over a stale
  // `auth`; react-oidc-context hands back a new object on every state change.
  const authRef = useRef(auth);
  authRef.current = auth;

  // Every request that hits the 401 raises its own event; the session ends once.
  const expiryTracked = useRef(false);

  useEffect(() => {
    const handleTokenExpired = (event: Event) => {
      const detail = (event as CustomEvent).detail ?? {};
      setTokenExpired(true);
      setExpiredMessage(detail.message ?? '');
      if (!expiryTracked.current) {
        expiryTracked.current = true;
        track('User Signed Out', { reason: 'expired' });
        resetAnalytics();
      }

      // Clearing sessionStorage is not enough on its own: the UserManager still
      // holds the user in memory with an `automaticSilentRenew` timer armed,
      // and the next renew writes the `oidc.user:*` entry straight back.
      // removeUser() is what actually stands the session down locally.
      void authRef.current.removeUser().catch((error) => {
        console.error('Failed to clear the expired session:', error);
      });
    };

    window.addEventListener('tokenExpired', handleTokenExpired);

    return () => {
      window.removeEventListener('tokenExpired', handleTokenExpired);
    };
  }, []);

  // The session is shared by every tab through localStorage, but each tab's
  // UserManager holds its own copy in memory. Follow what the other tabs do:
  // signed out there (the entry removed, or storage cleared) stands this tab
  // down; signed in there brings a signed-out tab in with a reload.
  useEffect(() => {
    const handleStorage = (event: StorageEvent) => {
      if (event.storageArea !== window.localStorage) return;

      const { settings, isAuthenticated } = authRef.current;
      const sessionKey = `oidc.user:${settings.authority}:${settings.client_id}`;
      if (event.key !== null && event.key !== sessionKey) return;

      const stored = window.localStorage.getItem(sessionKey) !== null;
      if (isAuthenticated && !stored) {
        void authRef.current.removeUser().catch((error) => {
          console.error('Failed to follow a sign-out in another tab:', error);
        });
      } else if (!isAuthenticated && stored) {
        window.location.reload();
      }
    };

    window.addEventListener('storage', handleStorage);

    return () => {
      window.removeEventListener('storage', handleStorage);
    };
  }, []);

  const handleReconnect = useCallback(async () => {
    setIsReconnecting(true);
    try {
      // Ends the Cognito session itself, not just the local copy of it.
      await auth.signoutRedirect({
        extraQueryParams: cognitoLogoutParams(auth.settings),
      });
    } catch (error) {
      console.error('Sign-out failed, falling back to a local reset:', error);
      try {
        await auth.removeUser();
        await auth.signinRedirect();
      } catch (fallbackError) {
        console.error('Failed to reconnect:', fallbackError);
      }
      // If neither redirect left the page we are still mounted, so give the
      // button back rather than stranding the user on a dead spinner.
      setIsReconnecting(false);
    }
  }, [auth]);

  // Kicking off a redirect is a side effect, so it belongs in an effect rather
  // than the render body — and behind a ref, so a re-render mid-navigation
  // cannot fire a second concurrent sign-in. With its own sign-in page the app
  // waits for the user instead.
  const signinStarted = useRef(false);
  const needsSignin =
    !signIn &&
    !auth.isLoading &&
    !auth.error &&
    !auth.isAuthenticated &&
    !tokenExpired &&
    !isReconnecting;

  useEffect(() => {
    if (!needsSignin || signinStarted.current) return;

    signinStarted.current = true;
    markSignIn('hosted_ui');
    auth.signinRedirect().catch((error) => {
      console.error('Failed to start sign-in:', error);
      signinStarted.current = false;
    });
  }, [needsSignin, auth]);

  // The own sign-in page has its own URL. Signed out anywhere else, move to
  // it, remembering the page asked for; signed in on it, go back to that page.
  // Both swap the URL in place: nothing is mounted that reads it yet, and the
  // sign-in screen renders the same either way. The Microsoft callback finishes
  // on its own URL and moves to the sign-in page itself.
  const [, rerender] = useReducer((count: number) => count + 1, 0);
  const sessionOver = tokenExpired || !!auth.error || !auth.isAuthenticated;
  const leaveForSignIn =
    !!signIn &&
    !auth.isLoading &&
    sessionOver &&
    !isLoginPage() &&
    !isMicrosoftCallback();
  const leaveSignInPage =
    !!signIn && !auth.isLoading && !sessionOver && isLoginPage();

  useEffect(() => {
    if (leaveForSignIn) {
      window.history.replaceState(null, '', loginUrl(signInReturnPath()));
    } else if (leaveSignInPage) {
      window.history.replaceState(null, '', signInReturnPath());
      rerender();
    }
  }, [leaveForSignIn, leaveSignInPage]);

  if (auth.isLoading || leaveSignInPage) {
    return (
      <div>
        <Loader fullscreen size={'lg'} />
      </div>
    );
  }

  /* The refresh token is gone: the session ended, it did not fail. */
  const sessionEnded = tokenExpired || isSessionEndedError(auth.error);
  const failed = !!auth.error && !sessionEnded;

  let notice: SignInNotice | undefined;
  if (failed) {
    notice = {
      color: 'danger',
      title: t('Authentication Error'),
      message:
        auth.error?.message ||
        t('An authentication error occurred. Please try logging in again.'),
    };
  } else if (sessionEnded) {
    notice = {
      color: 'warning',
      title: t('Session Expired'),
      message:
        expiredMessage || t('Your session has expired. Please log in again.'),
    };
  }

  if (signIn && (notice || !auth.isAuthenticated)) {
    // A Google sign-in Cognito refused comes back as an auth error carrying
    // the reason; the sign-in page explains it (and retries a fresh link).
    const refusal = auth.error
      ? signInRefusal(auth.error.message ?? '')
      : undefined;
    return <SignInScreen options={signIn} notice={notice} failure={refusal} />;
  }

  if (notice) {
    return (
      <SignInCard title={t('Sign in')} notice={notice}>
        {/* After a failure or an expiry the Cognito session cookie has to go
            too, or the hosted UI would sign the old session straight back in. */}
        <Button
          fullWidth
          startDecorator={<LoginIcon />}
          loading={isReconnecting}
          onClick={handleReconnect}
        >
          {failed ? t('Try Again') : t('Sign In Again')}
        </Button>
      </SignInCard>
    );
  }

  if (!auth.isAuthenticated) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: 'background.body',
        }}
      >
        <Sheet
          variant="soft"
          sx={{
            p: 4,
            borderRadius: 'lg',
            textAlign: 'center',
          }}
        >
          <Loader size="lg" />
          <Typography level="body-md" sx={{ mt: 2 }}>
            {t('Redirecting to sign-in...')}
          </Typography>
        </Sheet>
      </Box>
    );
  }

  return <>{children}</>;
};
